import { FC, useEffect, useMemo, useState } from "react";
import { Modal, Form, Input, Button, notification } from "antd";
import { ReimburseCategory, ReimburseCategoryAdd } from "@/types/reimburse";
import { add, subsAdd, update } from "@/apis/reimburse";
import { useTranslation } from "react-i18next";

type FormType = "add" | "update";

interface AddReimburseProps {
  open: boolean;
  data?: ReimburseCategory;
  formType: FormType;
  onSuccess: (formType: FormType, result: ReimburseCategory) => void;
  onClose: () => void;
}

const AddReimburse: FC<AddReimburseProps> = ({
  open,
  data,
  formType,
  onSuccess,
  onClose,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [api, contextHolder] = notification.useNotification({ placement: 'top' });

  const modalTitle = useMemo(() => {
    if (formType === "add") {
      return data ? t("common.addSub") : t("common.add");
    }
    return t("common.update");
  }, [formType, data, t]);

  useEffect(() => {
    if (open && formType === "update" && data) {
      form.setFieldsValue({
        code: data.code,
        name: data.name,
        desc: data.desc,
      });
    } else if (open && formType === "add") {
      form.resetFields();
    }
  }, [open, formType, data, form]);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const values = await form.validateFields();

      // 添加子类目时，拼接父节点的 code
      const finalCode = (formType === "add" && data)
        ? `${data.code}-${values.code}`
        : values.code;

      const params: ReimburseCategoryAdd = {
        code: finalCode,
        name: values.name,
        desc: values.desc || "",
        sortOrder: data?.sortOrder ?? 1,
      };

      if (formType === "add") {
        let result: ReimburseCategory;
        if (data) {
          // 添加子类目
          result = await subsAdd(data.id, params);
        } else {
          // 新增根类目
          result = await add(params);
        }
        api.success({
          message: t("common.addSuccess"),
          description: `${params.name} ${t("reimburse.addSuccessDesc")}`,
        });
        onSuccess("add", result);
      } else if (formType === "update" && data) {
        await update(data.id, params);
        api.success({
          message: t("common.updateSuccess"),
          description: `${params.name} ${t("reimburse.updateSuccessDesc")}`,
        });
        // update 返回 void，所以构造更新后的数据
        onSuccess("update", { ...data, ...params });
      }
    } catch (error: any) {
      console.error("Form submit error:", error);
      if (error === 69001) {
        api.error({
          message: t("reimburse.addFailed"),
          description: t("reimburse.codeDuplicate"),
        });
      } else {
        api.error({
          message: t("common.operationFailed"),
          description: t("common.pleaseTryAgain"),
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  return (
    <>
      {contextHolder}
      <Modal
        title={modalTitle}
        open={open}
        onCancel={handleCancel}
        maskClosable={false}
        destroyOnClose
        width={500}
        footer={
          <>
            <Button onClick={handleCancel}>{t("common.cancel")}</Button>
            <Button type="primary" onClick={handleSubmit} loading={loading}>
              {t("common.confirm")}
            </Button>
          </>
        }
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            label={t("common.code")}
            name="code"
            rules={[{ required: true, message: t("reimburse.plCode") }]}
          >
            <Input addonBefore={data?.code} placeholder={t("reimburse.plCode")} maxLength={50} disabled={formType === "update"} />
          </Form.Item>
          <Form.Item
            label={t("common.name")}
            name="name"
            rules={[{ required: true, message: t("reimburse.plName") }]}
          >
            <Input placeholder={t("reimburse.plName")} maxLength={100} />
          </Form.Item>
          <Form.Item label={t("common.desc")} name="desc">
            <Input.TextArea
              placeholder={t("common.plDesc")}
              autoSize={{ minRows: 3, maxRows: 6 }}
              maxLength={500}
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default AddReimburse;
